import { useEffect, useState, Fragment } from 'react';
import type { OutputNode } from '@/pages/home/outputFiles';
import { formatElapsed } from '@/pages/home/runFlow';
import MarkdownPreview from './MarkdownPreview';
import {
  assertionTitle,
  bestUserScore,
  buildRunScoreRows,
  formatContextTokens,
  modelBarTone,
  overlayLiveRun,
  scoreChartRuns,
  shortModelLabel,
  shortRunLabel,
  type RunScoreRecord,
  type ScoreCheck,
  type ScoreChartPoint,
} from '@/pages/home/runScorePanel';

type RunLog = { at: string; message: string };

type Props = {
  score: string;
  running?: boolean;
  runLogs?: RunLog[];
  elapsedMs?: number;
  liveRunId?: string | null;
  liveModel?: string;
  livePromptTokens?: number | null;
  userScore?: number | null;
  llmScore?: number | null;
  reviewMd?: string;
  scoreChecks?: ScoreCheck[];
  scoreHistory?: RunScoreRecord[];
  testArtifacts?: OutputNode[];
  onOpenArtifact?: (id: string) => void;
  scorePrompt?: boolean;
  canTrain?: boolean;
  trainEpoch?: number;
  onSubmitScore?: (score: number, note: string) => void | Promise<void>;
  onSkipScore?: () => void;
  onTrain?: () => void;
  onReviewNote?: (note: string) => void;
};

function scoreBarWidth(score: string): string {
  const n = parseFloat(score);
  if (!Number.isFinite(n)) return '0%';
  return `${Math.min(100, Math.max(0, (n / 12) * 100))}%`;
}

function scoreLabel(score: string): { num: string; den: string } {
  const slash = score.lastIndexOf('/');
  if (slash < 0) return { num: score, den: '12' };
  return { num: score.slice(0, slash), den: score.slice(slash + 1) };
}

function formatScore(n: number | null): string {
  return n == null ? '—' : `${n}/12`;
}

export default function TestsPanel({
  score, running = false, runLogs = [], elapsedMs = 0, liveRunId = null,
  liveModel = '', livePromptTokens = null,
  userScore = null, llmScore = null, reviewMd = '', scoreChecks = [],
  scoreHistory = [], testArtifacts = [], onOpenArtifact,
  scorePrompt = false, canTrain = false, trainEpoch = 0,
  onSubmitScore, onSkipScore, onTrain, onReviewNote,
}: Props) {
  const [scoreValue, setScoreValue] = useState(8);
  const [scoreNote, setScoreNote] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const best = bestUserScore(scoreHistory);
  const displayScore = best != null ? `${best}/12` : score;
  const displayLabel = scoreLabel(displayScore);
  const latest = scoreHistory[0];
  const rows = overlayLiveRun(scoreHistory, {
    runId: liveRunId,
    running,
    elapsedMs,
    userScore,
    llmScore,
    reviewMd,
    checks: scoreChecks,
    model: liveModel,
    promptTokens: livePromptTokens,
  });
  const chart = scoreChartRuns(rows);
  const defaultOpen = rows[0]?.runId || '';
  const [openId, setOpenId] = useState(defaultOpen);
  useEffect(() => { setOpenId(defaultOpen); }, [defaultOpen]);

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden" data-testid="score-tests-panel">
      <div className="shrink-0 px-5 py-3 border-b border-border flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-texts">Quality Score</span>
            <span className="text-sm font-mono text-textp" data-testid="quality-score">
              {displayLabel.num}<span className="text-texts">/{displayLabel.den}</span>
            </span>
          </div>
          <div className="h-2 bg-surface rounded-full overflow-hidden max-w-md">
            <div className="h-full bg-gradient-to-r from-accent to-success" style={{ width: scoreBarWidth(displayScore) }} />
          </div>
          {latest && best != null && latest.userScore != null && latest.userScore !== best ? (
            <div className="mt-1 text-[11px] text-texts">Latest {latest.userScore}/12</div>
          ) : null}
        </div>
        {canTrain && onTrain ? (
          <button onClick={onTrain} className="flex items-center gap-1 bg-accent hover:bg-accent-hover text-accent-text text-xs font-medium px-2.5 py-1.5 rounded-md whitespace-nowrap">
            <i className="ri-refresh-line" /> Train{trainEpoch > 0 ? ` ${trainEpoch + 1}/5` : ''}
          </button>
        ) : null}
      </div>

      <div className="flex-1 overflow-y-auto">
        {scorePrompt && onSubmitScore ? (
          <form
            className="mx-5 mt-4 space-y-2 border border-border rounded-md p-3 bg-surface/60"
            onSubmit={(e) => {
              e.preventDefault();
              if (submitting) return;
              const n = Number.isFinite(scoreValue) ? Math.min(12, Math.max(0, scoreValue)) : 0;
              setSubmitting(true);
              void Promise.resolve(onSubmitScore(n, scoreNote)).finally(() => setSubmitting(false));
            }}
          >
            <div className="text-xs text-textp font-medium">Score this output (0-12)</div>
            <input
              type="number"
              min={0}
              max={12}
              step={1}
              value={scoreValue}
              onChange={(e) => setScoreValue(Number(e.target.value))}
              disabled={submitting}
              className="w-full bg-bg border border-border rounded-md px-2 py-1 text-sm text-textp"
            />
            <textarea
              value={scoreNote}
              onChange={(e) => setScoreNote(e.target.value)}
              placeholder="Optional note"
              disabled={submitting}
              className="w-full bg-bg border border-border rounded-md px-2 py-1 text-sm text-textp resize-y min-h-[48px]"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={submitting}
                className="bg-accent hover:bg-accent-hover disabled:opacity-60 text-accent-text text-xs font-medium px-3 py-1.5 rounded-md"
              >
                {submitting ? 'Scoring…' : 'Submit score'}
              </button>
              {onSkipScore ? (
                <button type="button" onClick={onSkipScore} disabled={submitting} className="text-texts hover:text-textp text-xs px-2 py-1.5">Skip</button>
              ) : null}
            </div>
          </form>
        ) : null}

        {running && runLogs.length > 0 ? (
          <div className="mx-5 mt-4">
            <div className="text-[11px] uppercase tracking-wider text-texts mb-1.5">Run log</div>
            <ul className="space-y-1 max-h-36 overflow-y-auto font-mono text-[11px] text-texts border border-border rounded-md p-2.5 bg-surface/60">
              {runLogs.map((l, i) => (
                <li key={`${l.at}-${i}`} className="leading-snug">{l.message}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div data-testid="score-history" className="px-5 py-4">
          {chart.length > 0 ? <ScoreTimeChart points={chart} /> : null}
          <table className="w-full text-left border-collapse table-fixed">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-texts border-b border-border">
                <th className="py-2 pr-3 font-medium w-[14%]">Test run</th>
                <th className="py-2 pr-3 font-medium w-[18%]">Model</th>
                <th className="py-2 pr-3 font-medium whitespace-nowrap w-[12%]">Context</th>
                <th className="py-2 pr-3 font-medium whitespace-nowrap w-[12%]">Duration</th>
                <th className="py-2 pr-3 font-medium whitespace-nowrap w-[14%]">LLM score</th>
                <th className="py-2 pr-3 font-medium whitespace-nowrap w-[14%]">User score</th>
                <th className="py-2 font-medium w-[16%]">Artifacts</th>
              </tr>
            </thead>
            {rows.length === 0 && !running ? null : (
              <tbody>
                {rows.map((r) => {
                  const open = openId === r.runId;
                  const mark = r.userScore === best && best != null;
                  const live = running && r.runId === liveRunId;
                  return (
                    <Fragment key={r.runId}>
                      <tr
                        data-testid="score-history-run"
                        onClick={() => setOpenId(open ? '' : r.runId)}
                        className="border-b border-border/70 cursor-pointer hover:bg-surface/60"
                      >
                        <td className="py-2.5 pr-3 align-middle">
                          <span className="text-[12px] font-mono text-textp whitespace-nowrap" title={r.runId}>
                            {shortRunLabel(r.runId)}
                          </span>
                          {mark ? <span className="ml-1.5 text-[10px] uppercase text-success">best</span> : null}
                          {live ? <span className="ml-1.5 text-[10px] uppercase text-accent">running</span> : null}
                        </td>
                        <td className="py-2.5 pr-3 align-middle text-xs font-mono text-textp truncate" data-testid="run-model" title={r.model || undefined}>
                          {shortModelLabel(r.model)}
                        </td>
                        <td
                          className="py-2.5 pr-3 align-middle text-xs font-mono text-textp whitespace-nowrap"
                          data-testid="run-context"
                          title={r.promptTokens != null ? `${r.promptTokens.toLocaleString()} prompt tokens` : undefined}
                        >
                          {formatContextTokens(r.promptTokens)}
                        </td>
                        <td className="py-2.5 pr-3 align-middle text-xs font-mono text-textp whitespace-nowrap" data-testid="run-duration">
                          {live ? formatElapsed(elapsedMs) : r.durationMs != null ? formatElapsed(r.durationMs) : '—'}
                        </td>
                        <td className="py-2.5 pr-3 align-middle text-xs font-mono text-textp whitespace-nowrap" data-testid="run-llm-score">
                          {formatScore(r.llmScore)}
                        </td>
                        <td className="py-2.5 pr-3 align-middle text-xs font-mono text-textp whitespace-nowrap" data-testid="run-user-score">
                          {formatScore(r.userScore)}
                        </td>
                        <td className="py-2.5 align-middle">
                          {onOpenArtifact ? (
                            <button
                              type="button"
                              data-testid="open-run-artifacts"
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenArtifact(r.runId);
                              }}
                              className="text-[11px] text-accent hover:text-textp whitespace-nowrap"
                            >
                              Open artifacts
                            </button>
                          ) : null}
                        </td>
                      </tr>
                      {open ? (
                        <tr className="border-b border-border/70">
                          <td colSpan={7} className="pb-3 pt-1">
                            <ScoreCheckList checks={r.checks} />
                            {r.reviewMd ? (
                              <div className="mt-2 max-h-40 overflow-y-auto border border-border rounded-md bg-surface/60">
                                <MarkdownPreview content={r.reviewMd} compact />
                              </div>
                            ) : null}
                            {r.runId === liveRunId ? (
                              <TestArtifactList files={testArtifacts} onOpen={onOpenArtifact} />
                            ) : null}
                            {r.runId === liveRunId && userScore != null && onReviewNote ? (
                              <div className="mt-2 flex gap-2 max-w-xl">
                                <input
                                  value={followUp}
                                  onChange={(e) => setFollowUp(e.target.value)}
                                  placeholder="Follow-up note"
                                  className="flex-1 bg-surface/60 border border-border rounded-md px-2 py-1 text-xs text-textp"
                                />
                                <button
                                  onClick={() => { onReviewNote(followUp); setFollowUp(''); }}
                                  className="text-xs text-accent hover:text-textp whitespace-nowrap"
                                >
                                  Send
                                </button>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            )}
          </table>
          {rows.length === 0 && !running ? (
            <div className="py-10 text-sm text-texts">Click Run to execute the pipeline. Each run shows the model, context, duration, and scores. Expand a run for the LLM statements.</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ScoreTimeChart({ points }: { points: ScoreChartPoint[] }) {
  const modelOrder: string[] = [];
  const models = [...new Set(points.map((p) => p.model.trim() || 'unknown'))];
  for (const m of models) modelBarTone(m, modelOrder);
  return (
    <div data-testid="score-history-chart" className="mb-5">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] uppercase tracking-wider text-texts">Scores by run</div>
        <span className="text-[11px] text-texts">0-12, oldest first</span>
      </div>
      <div className="flex items-end gap-2 h-36 border-b border-border pb-0">
        {points.map((p) => {
          const height = Math.max(6, Math.round((p.score / 12) * 100));
          const tone = modelBarTone(p.model, modelOrder);
          const model = shortModelLabel(p.model);
          return (
            <div
              key={p.runId}
              data-testid="score-history-bar"
              data-run-id={p.runId}
              data-model={p.model || 'unknown'}
              className="flex-1 min-w-0 flex flex-col items-center justify-end h-full gap-1"
              title={`${p.label} ${model} ${p.score}/12`}
            >
              <span className="text-[10px] font-mono text-textp">{p.score}</span>
              <div className="w-full max-w-10 bg-surface2 rounded-t flex items-end justify-center" style={{ height: '100%' }}>
                <div className={`w-full ${tone} rounded-t`} style={{ height: `${height}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 mt-1.5">
        {points.map((p) => (
          <div key={`${p.runId}-label`} className="flex-1 min-w-0 text-center">
            <div className="text-[10px] font-mono text-texts truncate">{p.label}</div>
            <div className="text-[10px] text-texts truncate" title={p.model}>{shortModelLabel(p.model)}</div>
          </div>
        ))}
      </div>
      {models.length > 1 ? (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
          {models.map((m) => (
            <span key={m} className="inline-flex items-center gap-1.5 text-[10px] text-texts">
              <span className={`inline-block w-2 h-2 rounded-sm ${modelBarTone(m, modelOrder)}`} />
              {shortModelLabel(m)}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ScoreCheckList({ checks }: { checks: ScoreCheck[] }) {
  const rows = buildRunScoreRows(checks);
  if (!rows.length) return null;
  return (
    <div className="mt-2">
      <div className="text-[11px] uppercase tracking-wider text-texts mb-1.5">LLM statements</div>
      <ul data-testid="score-checks" className="space-y-1.5">
        {rows.map((row) => (
          <li key={row.name} data-testid="score-check" data-check-id={row.name} className="border border-border rounded-md px-2.5 py-2 bg-surface/60">
            <div className="flex items-center gap-1.5">
              <i className={`${row.passed ? 'ri-checkbox-circle-fill text-success' : 'ri-close-circle-fill text-error'} text-sm`} />
              <span className="text-xs font-medium text-textp">{assertionTitle(row.name)}</span>
            </div>
            {row.reason ? (
              <p data-testid="score-check-reason" className="mt-1 pl-5 text-[11px] text-texts leading-snug">{row.reason}</p>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TestArtifactList({ files, onOpen }: { files: OutputNode[]; onOpen?: (id: string) => void }) {
  if (!files.length) return null;
  return (
    <div data-testid="test-artifacts" className="mt-3">
      <div className="text-[11px] uppercase tracking-wider text-texts mb-1.5">Run artifacts</div>
      <ul className="space-y-1">
        {files.map((f) => (
          <li key={f.id}>
            <button
              type="button"
              data-testid="test-artifact"
              onClick={() => onOpen?.(f.id)}
              className="w-full flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-surface text-left"
            >
              <i className="ri-file-text-line text-sm text-texts" />
              <span className="text-xs text-textp truncate">{f.name}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
